import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface Call {
  _id: string;
  callerId: string;
  callerNumber: string;
  queueId: string;
  queueName: string;
  status: 'ringing' | 'answered' | 'ended' | 'missed';
  agentId?: string;
  agentName?: string;
  startTime: string;
  answerTime?: string;
  endTime?: string;
  duration?: number;
  priority: 'low' | 'medium' | 'high';
  recordingUrl?: string;
}

interface QueueRoutingContextType {
  activeCalls: Call[];
  waitingCalls: Call[];
  loading: boolean;
  error: string | null;
  fetchActiveCalls: () => Promise<void>;
  fetchWaitingCalls: () => Promise<void>;
  answerCall: (callId: string, agentId: string) => Promise<boolean>;
  endCall: (callId: string) => Promise<boolean>;
  transferCall: (callId: string, targetAgentId: string) => Promise<boolean>;
  holdCall: (callId: string) => Promise<boolean>;
  resumeCall: (callId: string) => Promise<boolean>;
  routeIncomingCall: (callerNumber: string, queueId: string) => Promise<Call>;
  getAvailableAgents: (queueId: string) => Promise<any[]>;
  createCallRecord: (callData: any) => Promise<any>;
}

const QueueRoutingContext = createContext<QueueRoutingContextType | undefined>(undefined);

export const useQueueRouting = () => {
  const context = useContext(QueueRoutingContext);
  if (!context) {
    throw new Error('useQueueRouting must be used within a QueueRoutingProvider');
  }
  return context;
};

interface QueueRoutingProviderProps {
  children: ReactNode;
}

export const QueueRoutingProvider: React.FC<QueueRoutingProviderProps> = ({ children }) => {
  const [activeCalls, setActiveCalls] = useState<Call[]>([]);
  const [waitingCalls, setWaitingCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch active calls
  const fetchActiveCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/calls/active');
      setActiveCalls(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Aktif çağrılar yüklenirken hata oluştu');
      console.error('Active calls fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch waiting calls
  const fetchWaitingCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/calls/waiting');
      setWaitingCalls(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Bekleyen çağrılar yüklenirken hata oluştu');
      console.error('Waiting calls fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get available agents for a queue
  const getAvailableAgents = async (queueId: string): Promise<any[]> => {
    try {
      const response = await api.get(`/queues/${queueId}/available-agents`);
      return response.data;
    } catch (err: any) {
      console.error('Available agents fetch error:', err);
      return [];
    }
  };

  // Route incoming call to available agent
  const routeIncomingCall = async (callerNumber: string, queueId: string): Promise<Call> => {
    try {
      setLoading(true);
      setError(null);

      // Get available agents for the queue
      const availableAgents = await getAvailableAgents(queueId);
      
      if (availableAgents.length === 0) {
        // No available agents, add to waiting queue
        const response = await api.post('/calls/waiting', {
          callerNumber,
          queueId,
          status: 'ringing',
          priority: 'medium'
        });
        
        const waitingCall = response.data;
        setWaitingCalls(prev => [...prev, waitingCall]);
        return waitingCall;
      }

      // Find the best available agent (round-robin or least busy)
      const selectedAgent = availableAgents[0]; // Simple selection for now
      
      // Create the call
      const response = await api.post('/calls', {
        callerNumber,
        queueId,
        agentId: selectedAgent._id,
        status: 'ringing',
        priority: 'medium'
      });

      const newCall = response.data;
      setActiveCalls(prev => [...prev, newCall]);

      return newCall;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Çağrı yönlendirilirken hata oluştu');
      console.error('Route incoming call error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Answer call
  const answerCall = async (callId: string, agentId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Update call status
      await api.put(`/calls/${callId}`, {
        status: 'answered',
        agentId,
        answerTime: new Date().toISOString()
      });

      // Update local state
      setActiveCalls(prev => prev.map(call => 
        call._id === callId 
          ? { ...call, status: 'answered', agentId, answerTime: new Date().toISOString() }
          : call
      ));

      // Remove from waiting calls if it was there
      setWaitingCalls(prev => prev.filter(call => call._id !== callId));

      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Çağrı cevaplanırken hata oluştu');
      console.error('Answer call error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // End call
  const endCall = async (callId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Update call status
      await api.put(`/calls/${callId}`, {
        status: 'ended',
        endTime: new Date().toISOString()
      });

      // Update local state
      setActiveCalls(prev => prev.map(call => 
        call._id === callId 
          ? { ...call, status: 'ended', endTime: new Date().toISOString() }
          : call
      ));

      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Çağrı sonlandırılırken hata oluştu');
      console.error('End call error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Transfer call
  const transferCall = async (callId: string, targetAgentId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Update call agent
      await api.put(`/calls/${callId}`, {
        agentId: targetAgentId
      });

      // Update local state
      setActiveCalls(prev => prev.map(call => 
        call._id === callId 
          ? { ...call, agentId: targetAgentId }
          : call
      ));

      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Çağrı aktarılırken hata oluştu');
      console.error('Transfer call error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Hold call
  const holdCall = async (callId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Update local state
      setActiveCalls(prev => prev.map(call => 
        call._id === callId 
          ? { ...call, status: 'on-hold' as any }
          : call
      ));

      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Çağrı bekletilirken hata oluştu');
      console.error('Hold call error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Resume call
  const resumeCall = async (callId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Update local state
      setActiveCalls(prev => prev.map(call => 
        call._id === callId 
          ? { ...call, status: 'answered' }
          : call
      ));

      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Çağrı devam ettirilirken hata oluştu');
      console.error('Resume call error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Auto-route waiting calls when agents become available
  useEffect(() => {
    const autoRouteWaitingCalls = async () => {
      if (waitingCalls.length === 0) return;

      for (const waitingCall of waitingCalls) {
        const availableAgents = await getAvailableAgents(waitingCall.queueId);
        
        if (availableAgents.length > 0) {
          const selectedAgent = availableAgents[0];
          await answerCall(waitingCall._id, selectedAgent._id);
          break; // Process one call at a time
        }
      }
    };

    // Check every 30 seconds
    const interval = setInterval(autoRouteWaitingCalls, 30000);
    return () => clearInterval(interval);
  }, [waitingCalls]);

  const createCallRecord = async (callData: any) => {
    try {
      const response = await api.post('/calls', callData);
      return response.data.data;
    } catch (error) {
      console.error('Create call record error:', error);
      throw error;
    }
  };

  const value: QueueRoutingContextType = {
    activeCalls,
    waitingCalls,
    loading,
    error,
    fetchActiveCalls,
    fetchWaitingCalls,
    answerCall,
    endCall,
    transferCall,
    holdCall,
    resumeCall,
    routeIncomingCall,
    getAvailableAgents,
    createCallRecord,
  };

  return (
    <QueueRoutingContext.Provider value={value}>
      {children}
    </QueueRoutingContext.Provider>
  );
};

export default QueueRoutingContext;
